import {
    dehydrate,
    QueryClient,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { GetServerSideProps, NextPage } from "next";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
// import adapterTourViewItemGAnalytics from "@/models/tour";
import {
    getBrochureData,
    getExcludedDaysSessionByTitle,
    getNavBarData,
    getPackageDetailByTitle,
    getRelatedByTitle,
    getVideoReviewsData,
} from "@/utils/queries";

import { TourResponseTO } from "@/models/detail";
import { OpenGraph } from "next-seo/lib/types";
// eslint-disable-next-line import/no-extraneous-dependencies
// import TagManager from "react-gtm-module";
// import { viewItem } from "@/utils/analytics/tagmanager";
import Seo from "@/components/common/Seo";
import Head from "next/head";
import { withRedirect } from "@/utils/redirects";
import { URLS } from "@/utils/constants";
import { useCurrency } from "@/utils/hooks/useCurrency";
import PackageDetailBody from "../../components/detail/PackageDetailBody";

const getOpenGraphData = (
    packageDetail: TourResponseTO | undefined
): OpenGraph => {
    if (packageDetail) {
        return {
            title: packageDetail.metadata?.title,
            description: packageDetail.metadata?.metaDescription,
            url: packageDetail.metadata?.urlCanonical,
            images: [
                {
                    url: packageDetail.quality.coverImage,
                    alt: packageDetail.metadata?.altHeadImage,
                },
                {
                    url: packageDetail.quality.whatWillYouSeeImage,
                    alt: packageDetail.metadata?.altWhatWillYouSeeImage,
                },
                ...(packageDetail.quality?.gallery ?? []).map(
                    (gallery, index) => ({
                        url: gallery,
                        alt: packageDetail.quality.galleryAlts?.at(index) || "",
                    })
                ),
            ],
        };
    }
    return {};
};

const PackageDetail: NextPage<{ isPremium: boolean }> = ({ isPremium }) => {
    const router = useRouter();
    const { slug } = router.query;
    const { currency } = useCurrency();
    const hasScrolledRef = useRef(false);
    const queryClient = useQueryClient();

    // const viewItemSent = useRef(false);

    // Ottieni i dati prefetchati dal server (senza currency nella queryKey)
    const prefetchedData = queryClient.getQueryData<TourResponseTO>([
        "packageDetailByTitle",
    ]);

    const { data: packageDetail } = useQuery({
        queryKey: ["packageDetailByTitle", slug?.[0], currency],
        queryFn: () => getPackageDetailByTitle(slug?.[0] as string),
        refetchInterval: false,
        enabled: !!slug?.[0],
        // Usa i dati prefetchati come placeholder mentre si carica la versione con currency
        placeholderData: prefetchedData,
        // Considera i dati "fresh" per 30 secondi per evitare ricaricamenti immediati
        staleTime: 30000,
    });

    // Reset hasScrolledRef quando cambia slug o currency
    useEffect(() => {
        hasScrolledRef.current = false;
    }, [slug?.[0], currency]);

    // Scroll in alto quando packageDetail viene caricato per la prima volta
    useEffect(() => {
        if (packageDetail && !hasScrolledRef.current) {
            hasScrolledRef.current = true;
            // Usa requestAnimationFrame per assicurarsi che il DOM sia renderizzato
            requestAnimationFrame(() => {
                window.scrollTo({ top: 0, behavior: "instant" });
            });
        }
    }, [packageDetail]);

    // useEffect(() => {
    //     if (packageDetail && !viewItemSent.current) {
    //         const prop = adapterTourViewItemGAnalytics(packageDetail);
    //         const events = viewItem(prop);
    //         events.forEach(eventObj => {
    //             TagManager.dataLayer({ dataLayer: eventObj });
    //         });
    //         viewItemSent.current = true;
    //     }
    // }, [packageDetail]);

    const metadata = getOpenGraphData(packageDetail);

    return (
        <>
            <Head>
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(
                            packageDetail?.metadata?.structuredData ?? ""
                        ),
                    }}
                />
            </Head>
            <Seo seoInfo={metadata} noindex={!packageDetail?.metadata?.index} />
            {packageDetail && (
                <PackageDetailBody
                    packageDetail={packageDetail}
                    initPremium={isPremium}
                />
            )}
        </>
    );
};

export const getServerSideProps: GetServerSideProps = withRedirect(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (ctx): Promise<any> => {
        const queryClient = new QueryClient();

        const isPremium =
            typeof ctx.query.isPremium === "string"
                ? ctx.query.isPremium
                : ctx.query.isPremium?.at(0);

        const booleanify = (value: string | undefined): boolean => {
            const truthy: string[] = ["true", "True", "1"];

            return truthy.includes(value ?? "false");
        };

        try {
            const tour = await queryClient.fetchQuery(
                ["packageDetailByTitle"],
                () => getPackageDetailByTitle(ctx.params!.slug![0])
            );

            if (tour.quality.status === "INACTIVE") {
                return {
                    redirect: {
                        statusCode: 301,
                        destination: URLS.allTours,
                    },
                };
            }
        } catch (e) {
            console.log(e);
        }

        await Promise.all([
            await queryClient.prefetchQuery(["navBar"], getNavBarData),
            await queryClient.prefetchQuery(["brochures"], getBrochureData),
            await queryClient.prefetchQuery(
                ["videoReview"],
                getVideoReviewsData
            ),
            await queryClient.prefetchQuery(
                ["relatedToursByTitle", ctx.params!.slug![0]],
                () => getRelatedByTitle(ctx.params!.slug![0])
            ),
            await queryClient.prefetchQuery(["packageDetailByTitle"], () =>
                getPackageDetailByTitle(ctx.params!.slug![0])
            ),
            await queryClient.prefetchQuery(
                ["exclusionDaysByTitle", ctx?.params?.slug?.[0]],
                () =>
                    getExcludedDaysSessionByTitle(
                        ctx?.params?.slug?.[0] || "",
                        true
                    )
            ),
        ]);

        return {
            props: {
                dehydratedState: dehydrate(queryClient),
                isPremium: booleanify(isPremium),
            },
        };
    }
);

export default PackageDetail;
